import { useMemo } from 'react';
import L from 'leaflet';
import { ColorScheme } from 'types/Meteorology/Agro/WaterBalanceSequential/Maps/colorScheme';
import { WBSequentialMapData } from 'types/Meteorology/Agro/WaterBalanceSequential/Maps/getMapData';
import { LegendRange } from 'types/Meteorology/Agro/WaterBalanceSequential/Maps/getOptions';
import useVariables from './useVariables';

export const DEFAULT_HEATMAP_COLOR_SCHEME: ColorScheme = {
  0.25: 'rgb(0,0,255)',
  0.55: 'rgb(0,255,0)',
  0.85: 'yellow',
  1.0: 'rgb(255,0,0)',
};

/**
 * Cria o HTML da legenda com faixas FIXAS definidas pelo backend.
 * Cada faixa tem um intervalo específico em mm (ex: 0-10, 10-20, etc.)
 */
export const addFixedLegendDiv = (
  legendRanges: LegendRange[],
  variableLabel: string | undefined,
  unit: string,
) => () => {
  const div = L.DomUtil.create('div', 'heatmap-legend');
  div.innerHTML = `<div class="text-nowrap">${variableLabel} (${unit})</div>`;

  legendRanges.forEach((range) => {
    const label = range.max === null
      ? `${range.min}+`
      : `${range.min}&ndash;${range.max}`;
    div.innerHTML += `<i style="background:${range.color}"></i> ${label}<br>`;
  });

  return div;
};

/**
 * Cria o HTML da legenda com faixas DINÂMICAS calculadas dos dados.
 * (comportamento original)
 */
export const addDynamicLegendDiv = (
  values: string[],
  colorScheme: ColorScheme,
  variableLabel: string | undefined,
  unit: string,
) => () => {
  const colorValues = [
    '0',
    ...Object.keys(colorScheme).sort((a, b) => parseFloat(a) - parseFloat(b)),
  ];
  const div = L.DomUtil.create('div', 'heatmap-legend');
  div.innerHTML = `<div class="text-nowrap">${variableLabel} (${unit})</div>`;
  for (let i = 0; i < colorValues.length; i += 1) {
    if (i === 0) {
      div.innerHTML += `<i style="background:${colorScheme[colorValues[1]]}"></i> ${values[0]}&ndash;${values[1]}<br>`;
    } else if (i === colorValues.length - 1) {
      div.innerHTML += `<i style="background:${colorScheme[colorValues[i]]}"></i> ${values[i]}+<br>`;
    } else {
      div.innerHTML += `<i style="background:linear-gradient(180deg,${colorScheme[colorValues[i]]} 0%, ${colorScheme[colorValues[i + 1]]} 100%);"></i> ${values[i]}&ndash;${values[i + 1]}<br>`;
    }
  }
  return div;
};

/** @deprecated Use addDynamicLegendDiv instead */
export const addLegendDiv = addDynamicLegendDiv;

export default function useHeatmapLegend(data?: WBSequentialMapData) {
  const { variableLabel, unit, config } = useVariables();

  return useMemo(() => {
    if (!data || !data.length || !variableLabel) return undefined;

    const legend = new L.Control({ position: 'bottomright' });

    // Se há faixas fixas definidas no config, usa-as
    if (config?.legendRanges && config.legendRanges.length > 0) {
      legend.onAdd = addFixedLegendDiv(config.legendRanges, variableLabel, unit);
      return legend;
    }

    // Fallback: comportamento dinâmico original
    const colorScheme = config?.gradient || DEFAULT_HEATMAP_COLOR_SCHEME;
    const values = data.map(([,, value]) => value);
    const maxValue = Math.max(...values);
    const minValue = Math.min(...values);
    const unnormalizedColorValues = Object.keys(colorScheme).map(Number).map((key) => (
      key * (maxValue - minValue) + minValue
    ));
    const legendValues = [
      0,
      ...unnormalizedColorValues.sort((a, b) => a - b),
    ].map((value) => value.toFixed(1));
    legend.onAdd = addDynamicLegendDiv(legendValues, colorScheme, variableLabel, unit);

    return legend;
  }, [data, variableLabel, unit, config]);
}
